import { useLayoutEffect, useState } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'
import { ScreenName } from '@/helpers/telemetry/ScreenName'

/**
 * Determines the screenName for analytics tracking based on the current pathname
 */
export function useAnalyticsScreenName({
  pathname,
  hasHomeIntent,
}: {
  pathname: string | null
  hasHomeIntent: boolean
}): string {
  const isAlpAsFrontpage = useFlag(FeatureFlags.ALP_AS_FRONTPAGE)

  // isAlpAtRoot: ALP_AS_FRONTPAGE is on and the user has no escape-hatch cookie, so the
  // root URL "/" is serving ALP content instead of the homepage.
  // layout.tsx does not re-run on client-side navigation (Next.js preserves layouts),
  // so hasHomeIntent can be stale after a logo click sets the cookie. Re-read the cookie
  // client-side after each navigation so isAlpAtRoot stays accurate.
  const [isAlpAtRoot, setIsAlpAtRoot] = useState(
    isAlpAsFrontpage && !hasHomeIntent
  )
  useLayoutEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setIsAlpAtRoot(
      isAlpAsFrontpage && !document.cookie.includes('bstock_hp_intent=1')
    )
  }, [isAlpAsFrontpage, pathname])

  if (!pathname || pathname === '/') {
    return isAlpAtRoot ? ScreenName.AllAuction : ScreenName.Homepage
  }

  // Listing detail pages
  if (pathname.match(/\/buy\/listings\/details\/|\/listings\/details\//)) {
    return ScreenName.ListingDetails
  }

  // Watchlist pages
  if (pathname.match(/\/buy\/p\/[^/]+\/watchlist/)) {
    return ScreenName.Watchlist
  }

  // Seller storefront
  if (pathname.match(/\/buy\/seller\/[^/]+/)) {
    return ScreenName.SellerStorefront
  }

  if (pathname === '/first-dibs') {
    return ScreenName.FirstDibs
  }

  // All auctions pages
  if (pathname.startsWith('/all-auctions')) {
    return ScreenName.AllAuction
  }

  // Order pages - check specific routes before general ones
  if (pathname.match(/\/buy\/user\/[^/]+\/orders\/[^/]+\/create-dispute/)) {
    return ScreenName.CreateDispute
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/orders\/[^/]+\/triage/)) {
    return ScreenName.OrderTriage
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/orders\/details\/[^/]+/)) {
    return ScreenName.OrderDetails
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/orders\/legacy/)) {
    return ScreenName.LegacyOrders
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/orders\/?$/)) {
    return ScreenName.AllOrders
  }

  // Contract pages - check details before list
  if (pathname.match(/\/buy\/user\/[^/]+\/contracts\/details\/[^/]+/)) {
    return ScreenName.ContractDetails
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/contracts\/?$/)) {
    return ScreenName.MyContracts
  }

  // Dispute pages - check details before list
  if (pathname.match(/\/buy\/user\/[^/]+\/disputes\/details\/[^/]+/)) {
    return ScreenName.DisputeDetails
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/disputes\/?$/)) {
    return ScreenName.MyDisputes
  }

  // Other user account pages
  if (pathname.match(/\/buy\/user\/[^/]+\/offers\/?$/)) {
    return ScreenName.MyOffers
  }
  if (pathname.match(/\/buy\/user\/[^/]+\/bids\/?$/)) {
    return ScreenName.MyBids
  }

  // Error page
  if (pathname === '/_error') {
    return ScreenName.Error
  }

  // Default fallback to unknown for unrecognized routes
  return ScreenName.Unknown
}
